import { Badge, Box, Flex, HStack, Image, Text, VStack } from "@chakra-ui/react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { Messages } from "../../core/api";
import { auth } from "../../core/auth";
import { env } from "../../env";
import useApi from "../../shared/hooks/useApi";
import { queryKeys } from "../../shared/query-keys";
import { PatientId } from "../../shared/schema/schema";
import EntityProfileModalFrame from "../entityProfileModal/components/EntityProfileModalFrame";
import { EntityProfileEmptyState } from "../entityProfileModal/components/EntityProfileFeedback";
import { entityProfileModalColors } from "../entityProfileModal/entityProfileModal.theme";
import useEntityProfileSectionNavigation from "../entityProfileModal/hooks/useEntityProfileSectionNavigation";
import PatientProfileHeader from "./components/PatientProfileHeader";
import PatientProfileNavigation from "./components/PatientProfileNavigation";
import PatientProfileSections from "./components/PatientProfileSections";
import { usePatientProfileAiCapabilities } from "./patientProfileModal.ai";
import { mapPatientProfileDisplayData } from "./patientProfileModal.mocks";
import { patientProfileBridgeActions } from "./patientProfileModal.actions";
import { patientProfileSections } from "./patientProfileModal.sections";
import type {
  PatientProfileSection,
  PatientProfileSubsection,
} from "./patientProfileModal.sections";
import { PatientProfilePatient } from "./patientProfileModal.types";

type Props = {
  patientId: PatientId;
  showBillingFeature: boolean;
  onClose: () => void;
  onReady?: () => void;
};

type PatientCustomAddressChangeDetail = {
  type: "customAddressChange";
  payload: {
    entity: "Patient";
    id: PatientProfilePatient["id"];
    address: string;
    timezone: string;
    addressComponents: Messages["AddressComponents"];
  };
};

type PatientPhoneNumbersChangeDetail = {
  type: "patientProfilePhoneNumbersChange";
  payload: {
    patientId: PatientProfilePatient["id"];
  };
};

function canViewSubsection(subsection: PatientProfileSubsection, showBillingFeature: boolean) {
  if (subsection.requiresBillingFeature === true && showBillingFeature === false) {
    return false;
  }

  if (subsection.isOnboardingOnly === true) {
    const authState = auth.getAuthState();
    const agencyId = authState.type === "authenticated" ? authState.data.agency.id : null;

    if (agencyId !== env.MEDFLYT_AGENCY_ID) {
      return false;
    }
  }

  if (subsection.permissionKey === undefined) {
    return true;
  }

  return subsection.permissionKey
    .split("|")
    .some((permissionKey) => auth.isAllowedTo(permissionKey as Messages["AgencyMemberPermission"]));
}

function canViewSection(
  section: PatientProfileSection,
  patient: PatientProfilePatient | undefined
) {
  if (patient === undefined) {
    return true;
  }

  return section.hiddenPatientStatuses?.includes(patient.status) !== true;
}

function getVisibleSections(
  sections: PatientProfileSection[],
  patient: PatientProfilePatient | undefined,
  showBillingFeature: boolean
) {
  return sections
    .filter((section) => canViewSection(section, patient))
    .map((section) => ({
      ...section,
      subsections: section.subsections.filter((subsection) =>
        canViewSubsection(subsection, showBillingFeature)
      ),
    }))
    .filter((section) => section.subsections.length > 0);
}

function PatientProfileLoadingHeader(props: { patientId: PatientId }) {
  return (
    <Flex
      alignItems="flex-start"
      gap={4}
      justifyContent="space-between"
      px={{ base: 4, md: 6, xl: 8 }}
      py={4}
    >
      <Flex gap={4} minW={0}>
        <Image
          alt=""
          borderRadius="full"
          boxSize={{ base: "64px", md: "70px" }}
          flexShrink={0}
          objectFit="cover"
          src="admin/images/avatars/avatar_placeholder.png"
        />
        <VStack alignItems="flex-start" gap={1} minW={0}>
          <HStack alignItems="center" flexWrap="wrap" spacing={2}>
            <Text
              color={entityProfileModalColors.hoverTitle}
              fontSize={{ base: "22px", md: "25px" }}
              fontWeight={600}
              lineHeight="1.1"
            >
              Patient #{props.patientId}
            </Text>
            <Badge borderRadius="2px" colorScheme="blue" fontSize="11px" px={2} py={1}>
              Loading
            </Badge>
          </HStack>
          <Text color={entityProfileModalColors.muted} fontSize="14px" lineHeight="1.25">
            Loading patient profile details...
          </Text>
        </VStack>
      </Flex>
    </Flex>
  );
}

function PatientProfileContent(props: {
  patient?: PatientProfilePatient;
  patientId: PatientId;
  showBillingFeature: boolean;
}) {
  const queryClient = useQueryClient();
  const patient = props.patient;

  const displayData = React.useMemo(
    () =>
      patient === undefined
        ? {
            addressText: "",
            agencyTeamName: "",
            assignedCoordinatorName: "",
            currentOfficeName: "",
          }
        : mapPatientProfileDisplayData(patient),
    [patient]
  );
  const visibleSections = React.useMemo(
    () => getVisibleSections(patientProfileSections, patient, props.showBillingFeature),
    [patient, props.showBillingFeature]
  );
  const sectionNavigation = useEntityProfileSectionNavigation({
    entity: "patient",
    sectionIdPrefix: "react-patient-section",
    sections: visibleSections,
  });
  // This window can drive the patient-profile capabilities while it is the one on screen. The
  // visible sections are what the AI is offered, so it never suggests a section this user cannot see.
  usePatientProfileAiCapabilities(patient, sectionNavigation, visibleSections);

  const activeSection = sectionNavigation.activeSection;
  const hasActiveSection = activeSection !== undefined;
  const handleOpenCalendar = React.useCallback(() => {
    const sectionIndex = visibleSections.findIndex((section) => section.label === "Calendar");

    if (sectionIndex < 0) {
      return;
    }

    sectionNavigation.handleSectionChange(sectionIndex);
  }, [sectionNavigation, visibleSections]);

  React.useEffect(() => {
    if (patient === undefined) {
      return;
    }

    const invalidatePatient = () => {
      const queryKey = queryKeys.patient.get(patient.id);

      queryClient.invalidateQueries({ queryKey });
      queryClient.refetchQueries({ queryKey, type: "active" });
    };
    const handlePatientProfileChange = (event: Event) => {
      const detail = (
        event as CustomEvent<PatientCustomAddressChangeDetail | PatientPhoneNumbersChangeDetail>
      ).detail;

      if (
        detail?.type === "patientProfilePhoneNumbersChange" &&
        detail.payload.patientId === patient.id
      ) {
        invalidatePatient();
        return;
      }

      if (
        detail?.type !== "customAddressChange" ||
        detail.payload.entity !== "Patient" ||
        detail.payload.id !== patient.id
      ) {
        return;
      }

      const queryKey = queryKeys.patient.get(patient.id);

      queryClient.setQueryData(queryKey, (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          address: {
            text: detail.payload.address,
            timezone: detail.payload.timezone,
            components: detail.payload.addressComponents,
          },
          isCustomAddress: true,
        };
      });
      invalidatePatient();
    };

    window.addEventListener("from-webapp-react", handlePatientProfileChange);

    return () => window.removeEventListener("from-webapp-react", handlePatientProfileChange);
  }, [patient, queryClient]);

  return (
    <Flex bg={entityProfileModalColors.surface} direction="column" h="100%" minH={0}>
      <Box bg={entityProfileModalColors.surface}>
        {patient === undefined ? (
          <PatientProfileLoadingHeader patientId={props.patientId} />
        ) : (
          <PatientProfileHeader
            actions={patientProfileBridgeActions}
            displayData={displayData}
            patient={patient}
            showBillingFeature={props.showBillingFeature}
            onOpenCalendar={handleOpenCalendar}
          />
        )}
        {hasActiveSection ? (
          <PatientProfileNavigation
            activeSectionIndex={sectionNavigation.activeSectionIndex}
            activeSubsectionIndex={sectionNavigation.activeSubsectionIndex}
            sections={visibleSections}
            onSectionChange={sectionNavigation.handleSectionChange}
            onSubsectionChange={sectionNavigation.handleSubsectionChange}
          />
        ) : null}
      </Box>

      <Box
        ref={sectionNavigation.contentRef}
        bg={entityProfileModalColors.surface}
        flex={1}
        minH={0}
        overflowY="auto"
        pb="56px"
        scrollPaddingBottom="56px"
        onScroll={sectionNavigation.handleContentScroll}
      >
        {activeSection === undefined ? (
          <Box px={5} py={6}>
            <EntityProfileEmptyState>No profile sections are available.</EntityProfileEmptyState>
          </Box>
        ) : (
          <PatientProfileSections
            key={props.patientId}
            actions={patientProfileBridgeActions}
            displayData={displayData}
            isProfileLoading={patient === undefined}
            patient={patient}
            patientId={props.patientId}
            section={activeSection}
            showBillingFeature={props.showBillingFeature}
          />
        )}
      </Box>
    </Flex>
  );
}

export default function PatientProfileModal(props: Props) {
  const { queries } = useApi();
  const { onReady } = props;
  const didNotifyReadyRef = React.useRef(false);
  const patientQuery = useQuery({
    ...queries.patient.get(props.patientId),
    placeholderData: keepPreviousData,
  });

  React.useEffect(() => {
    if (
      didNotifyReadyRef.current === false &&
      (patientQuery.data !== undefined || patientQuery.isError)
    ) {
      didNotifyReadyRef.current = true;
      onReady?.();
    }
  }, [onReady, patientQuery.data, patientQuery.isError]);

  return (
    <EntityProfileModalFrame
      entityLabel="patient"
      isError={patientQuery.isError}
      isLoading={patientQuery.isLoading || patientQuery.data === undefined}
      onClose={props.onClose}
      onRetry={() => void patientQuery.refetch()}
    >
      <PatientProfileContent
        patient={patientQuery.data}
        patientId={props.patientId}
        showBillingFeature={props.showBillingFeature === true}
      />
    </EntityProfileModalFrame>
  );
}
